import React, { useState, useRef, useEffect } from 'react';
import { isEarlyGame, isNoCoverage, getMinutesPlayed, saveLineToCache, getLineFromCache, getDisplayMinJugados, periodLabel, esHandballPick } from '../utils';
import LineStatusBadge from './LineStatusBadge';
import Tooltip from './Tooltip';

const WAIT_STATES = ['no bet','no under','no over','jugable','wait','esperar','salir','sin línea','sin linea'];
function isWaitDecision(d) { return WAIT_STATES.some(w => d.includes(w)); }

function getVerdictClass(dec) {
  const d = (dec || '').toLowerCase();
  if (isWaitDecision(d)) return 'wait';
  if (d.includes('early') || d.includes('temprano')) return 'early';
  if (d.includes('late')) return 'late';
  if (d.includes('fuerte')) return d.includes('under') ? 'fuerte-under' : 'fuerte-over';
  if (d.includes('under')) return 'under';
  if (d.includes('over')) return 'over';
  return 'wait';
}

function getVerdictLabel(dec) {
  const d = (dec || '').toLowerCase();
  if (isWaitDecision(d)) return d.includes('sin línea') || d.includes('sin linea') ? 'SIN LÍNEA' : 'ESPERAR';
  if (d.includes('early') || d.includes('temprano')) return 'TEMPRANO';
  if (d.includes('late')) return 'TARDÍO';
  if (d.includes('fuerte')) return d.includes('under') ? 'UNDER ★' : 'OVER ★';
  if (d.includes('under') || d.includes('over')) return 'VIGILAR';
  return 'ESPERAR';
}

function parseStake(stake) {
  if (stake === null || stake === undefined) return 0;
  const match = String(stake).trim().match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
}

// Mapea etiqueta de veredicto a data-v del mockup
function toTone(label) {
  if (label.includes('★')) return 'ENTRAR';
  if (label.includes('VIGILAR')) return 'VIGILAR';
  return 'ESPERAR';
}

function getClockMin(pick) {
  const c = pick.clock;
  if (c === null || c === undefined) return null;
  if (typeof c === 'number') return c;
  const s = String(c).trim().toUpperCase();
  if (['HT','BT','HALF','HALFTIME','BREAK','FT','FINAL','END',''].includes(s)) return null;
  if (s.includes(':')) {
    const [m, sec] = s.split(':');
    const mi = parseInt(m, 10), si = parseInt(sec, 10);
    if (Number.isFinite(mi) && Number.isFinite(si)) return mi + si / 60;
    return null;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export default function GameRow({ pick, selected, onClick, ageBadge, tick, isLast, onLineChange, tgSent, tgLine, dataTour }) {
  const lineaRaw  = pick.linea ?? pick.line ?? pick.over_under ?? pick.ou ?? null;
  const sourceRaw = pick.linea_source ?? pick.line_source ?? pick.source ?? null;
  const tsRaw     = pick.linea_ts ?? pick.line_ts ?? null;

  // Persistir línea válida en localStorage (TTL 5 min) para que sobreviva refreshes
  const persistedCache = pick.id ? getLineFromCache(pick.id) : null;
  if (lineaRaw && parseFloat(lineaRaw) > 0 && pick.id) {
    saveLineToCache(pick.id, lineaRaw, sourceRaw, tsRaw);
  }
  // Línea efectiva: prioridad → línea actual de Firebase, después caché persistente
  const effectiveLinea  = lineaRaw && parseFloat(lineaRaw) > 0 ? lineaRaw  : (persistedCache?.linea  ?? null);
  const effectiveSource = lineaRaw && parseFloat(lineaRaw) > 0 ? sourceRaw : (persistedCache?.source ?? null);
  const effectiveTs     = lineaRaw && parseFloat(lineaRaw) > 0 ? tsRaw     : (persistedCache?.ts     ?? null);
  const isStale         = !lineaRaw && persistedCache; // línea en caché persistente, no fresca

  function normalizeSource(s) {
    if (!s) return null;
    return String(s).toLowerCase() === 'manual' ? 'manual' : 'API';
  }
  const sourceNorm = normalizeSource(effectiveSource);

  const [line, setLine]       = useState(effectiveLinea ? String(effectiveLinea) : '');
  // Diferencia derivada DIRECTAMENTE de la prop en cada render. Antes
  // estaba en useState (fósil del input manual ya removido) y se quedaba
  // congelada al primer render: la celda 📊 Diferencia no refrescaba hasta
  // que el usuario hacía F5. Reportado por tester Esteban 2026-05-06 21:49.
  const edge = pick.edge ?? null;

  // Calcular veredicto inicial — misma lógica que DetailPanel
  const initial = (() => {
    const hasTrampas = typeof pick.trampas === 'number' && pick.trampas > 0;
    const noStake    = parseStake(pick.stake) === 0;
    if (hasTrampas && noStake) return { label: 'ESPERAR', cls: 'wait' };

    const fbEdge      = parseFloat(pick.edge || 0);
    const hasLine     = effectiveLinea && parseFloat(effectiveLinea) > 0;
    const timingGo    = (pick.timing || '').toUpperCase() === 'GO';
    const stakeOk     = parseStake(pick.stake) > 0;
    const canBeFuerte = timingGo && stakeOk;

    // Si hay línea válida, siempre calcular veredicto por edge
    // NUNCA mostrar SIN LÍNEA cuando hay línea en Firebase
    if (hasLine) {
      if (fbEdge === 0) return { label: 'ESPERAR', cls: 'wait' };
      if (fbEdge <= -8 && canBeFuerte)  return { label: 'UNDER ★', cls: 'fuerte-under' };
      if (fbEdge >= 8  && canBeFuerte)  return { label: 'OVER ★',  cls: 'fuerte-over'  };
      if (fbEdge <= -6)                 return { label: 'VIGILAR', cls: 'under' };
      if (fbEdge >= 6)                  return { label: 'VIGILAR', cls: 'over'  };
      return { label: 'ESPERAR', cls: 'wait' };
    }

    // Sin línea → usar decision del bot
    return { label: getVerdictLabel(pick.decision), cls: getVerdictClass(pick.decision) };
  })();
  const [verdict, setVerdict]           = useState(initial.label);
  const [verdictClass, setVerdictClass] = useState(initial.cls);
  const [hasManualLine, setHasManualLine] = useState(false);

  const proj   = parseFloat(pick.proyeccion || 0);
  const isEarly = isEarlyGame(pick);          // TEMPRANO: < 15 min jugados o decision early o dataConf < 25%
  const isNoCov = isNoCoverage(pick);         // SIN COBERTURA: >= 2 min sin línea API
  const conf   = parseFloat(pick.confianza || 0);
  const noLineInFirebase = (!effectiveLinea || parseFloat(effectiveLinea) <= 0);

  // FIX: ya NO recalculamos el edge en el frontend.
  // El pick.edge de Firebase ya viene con el confidence aplicado desde el engine Python.
  // Recalcularlo como (proyeccion - linea) ignora el confidence y da valores crudos incorrectos.
  const cachedEdge = null;

  // ── RED DE SEGURIDAD: bloqueo HT/BT ────────────────────────
  // Si el partido está en entretiempo o break, forzamos ESPERAR
  // independientemente del veredicto que venga de Firebase.
  // Esto previene mostrar señales fuertes durante HT/BT cuando el
  // modelo es inestable por falta de reloj corriendo.
  const clockUpper = String(pick.clock || '').toUpperCase().trim();
  const isHalftime = ['HT','BT','HALF','HALFTIME','BREAK'].includes(clockUpper);

  const displayVerdict = (() => {
    if (isHalftime) return 'ESPERAR';
    if (isNoCov) return 'SIN COBERTURA';
    if (isEarly) return 'TEMPRANO';
    // FIX: la rama 'SIN LÍNEA' fue eliminada — ahora <LineStatusBadge>
    // muestra el estado de la línea con contador. El verdict cae al
    // default cuando no hay línea (típicamente ESPERAR del bot).
    return verdict;
  })();
  const displayVerdictClass = (() => {
    if (isHalftime) return 'wait';
    if (isNoCov) return 'wait';
    if (isEarly) return 'early';
    return verdictClass;
  })();
  const displayEdge         = noLineInFirebase
    ? (cachedEdge !== null ? cachedEdge : null)
    : edge;
  const hasRealLine = !noLineInFirebase || cachedEdge !== null;

  const edgeVal = hasRealLine ? (displayEdge ?? pick.edge ?? 0) : 0;
  // DIFERENCIA = proyección − línea (cruda). Antes mostraba pick.edge, que viene
  // ajustado por confianza_datos en el motor y no cuadraba con proy−línea. El
  // edge ajustado SE SIGUE usando para el veredicto (fbEdge); esto es solo display.
  const _lineNum = parseFloat(effectiveLinea);
  const _crudaDiff = (proj > 0 && _lineNum > 0) ? (proj - _lineNum) : null;
  const edgeDisplay = isHalftime || isEarly || isNoCov ? '—'
    : (_crudaDiff !== null && !isNaN(_crudaDiff))
      ? `${_crudaDiff >= 0 ? '+' : ''}${_crudaDiff.toFixed(1)}`
      : '—';

  // Diseño neutro: la celda Diferencia (📊) va siempre en tono `neu`. El
  // color (verde/azul) lo lleva exclusivamente la pill de recomendación.
  const edgeClass = 'neu';

  // Lógica de visibilidad de proyección + diferencia. Dashboard v5:
  //   - Q3 desde minuto 3 en adelante: visible
  //   - Q4 (cualquier clock): visible
  //   - Alerta TG enviada (cualquier cuarto): visible
  //   - DESCARTADO (cliente decide con info completa): visible
  //   - resto: pill coloquial "👀 Tranqui, ya verás mi predicción"
  const periodNum   = Number(pick.period) || 0;
  const clockMinNum = getClockMin(pick);
  const isDescartado = (
    pick.v6?.categoria === 'DESCARTADO' ||
    pick.categoria_dashboard === 'DESCARTADO'
  );
  // 2026-05-21: proyección visible en TODOS los partidos desde el minuto 1 del Q1.
  // (antes: oculta con pill "Tranqui, ya verás mi predicción" hasta Q3≥min3 / Q4 /
  // TG enviada / DESCARTADO). Si aún no hay proyección (proj<=0) se muestra "—".
  const showProjection = true;

  function getProjTooltip() {
    if (isDescartado) {
      return 'Esta liga yo como motor V6 la descarto, pero la proyección te la muestro como referencia';
    }
    if (periodNum === 4) {
      return 'Mi predicción en Q4 es más precisa, tengo toda la información que necesito';
    }
    return 'Estoy proyectando, pero mi predicción en Q4 es más precisa';
  }

  // Auto-actualizar veredicto cuando cambia el edge o línea de Firebase
  useEffect(() => {
    if (hasManualLine) return; // no sobreescribir si el usuario ingresó línea manual
    const fbEdge   = parseFloat(pick.edge || 0);
    const hasLine  = effectiveLinea && parseFloat(effectiveLinea) > 0;
    const timingGo = (pick.timing || '').toUpperCase() === 'GO';
    const stakeOk  = parseStake(pick.stake) > 0;
    const canBeFuerte = timingGo && stakeOk;

    if (!hasLine || fbEdge === 0) return;

    if (fbEdge <= -8 && canBeFuerte)  { setVerdict('UNDER ★'); setVerdictClass('fuerte-under'); }
    else if (fbEdge >= 8 && canBeFuerte)  { setVerdict('OVER ★');  setVerdictClass('fuerte-over'); }
    else if (fbEdge <= -6)            { setVerdict('VIGILAR'); setVerdictClass('under'); }
    else if (fbEdge >= 6)             { setVerdict('VIGILAR'); setVerdictClass('over'); }
    else                              { setVerdict('ESPERAR'); setVerdictClass('wait'); }
  }, [pick.edge, effectiveLinea, pick.timing, pick.stake]);

  // FIX: useEffect de cachedEdge eliminado.
  // Como cachedEdge siempre es null ahora, este bloque no hacía nada útil
  // y además propagaba un edge recalculado incorrecto a lineOverride en DetailPanel.

  // FIX: función recalc eliminada — el input manual fue removido.
  // El edge ahora siempre viene de Firebase (pick.edge) calculado por el engine Python.

  // Parsing score del partido
  const [awayScore, homeScore] = (() => {
    const s = pick.score || '';
    const parts = s.split('-').map(x => parseInt(x.trim(), 10));
    return [parts[0] || 0, parts[1] || 0];
  })();
  const homeWin = homeScore > awayScore;

  const tone = toTone(displayVerdict);

  // FASE V7 — categoría calculada por el bot en /live_games. Refleja
  // BLACKLIST/whitelist, ventana temporal, edge-threshold y guards V6.
  // Si llegó, manda sobre la lógica local (que solo conoce edge cliente-side).
  const v6Categoria = pick.v6?.categoria || pick.categoria_dashboard || null;
  const v6Razon     = pick.v6?.razon_humana || pick.razon_humana || null;

  const isBlocked = isEarly || isNoCov || isHalftime;

  return (
    <tr
      className="match"
      data-tour={dataTour}
      data-v={
        v6Categoria === 'ENTRAR'                                ? 'ENTRAR'
        : v6Categoria === 'VIGILAR'                             ? 'VIGILAR'
        : v6Categoria === 'DESCARTADO'                          ? 'DESCARTAR'
        : v6Categoria                                            ? 'ESPERAR'
        : tone
      }
      data-open={selected ? "true" : "false"}
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      {/* PARTIDO */}
      <td>
        <div className="teams">
          <div className="tline">
            {pick.away_logo && (
              <img src={pick.away_logo} alt="" style={{ width:18, height:18, objectFit:'contain', flexShrink:0 }}
                onError={e => e.currentTarget.style.display='none'} />
            )}
            <span className="team-names">{pick.partido || '—'}</span>
            {pick.home_logo && (
              <img src={pick.home_logo} alt="" style={{ width:18, height:18, objectFit:'contain', flexShrink:0 }}
                onError={e => e.currentTarget.style.display='none'} />
            )}
            {ageBadge}
            {pick.period > 0 && (
              <span className="live-pill">
                <span className="live-dot" /> En vivo
              </span>
            )}
          </div>
          <div className="meta">
            <span className="q">{esHandballPick(pick)
              ? periodLabel(pick)
              : `Q${pick.period || '?'}${pick.min_jugados ? ` · min ${getDisplayMinJugados(pick)}` : (pick.clock ? ` · ${pick.clock}` : '')}`}</span>
            <span className="sep">·</span>
            <span>{pick.liga || ''}</span>
            {pick.pace_source && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 3,
                  letterSpacing: '0.03em',
                  background: pick.pace_source === 'learned'
                    ? 'var(--pos-soft)'
                    : 'var(--warn-soft)',
                  color: pick.pace_source === 'learned'
                    ? 'var(--pos)'
                    : 'var(--warn)',
                }}
                title={pick.pace_source === 'learned'
                  ? 'Pace calculado con datos reales (≥5 partidos en SQLite)'
                  : 'Pace por defecto (fallback del LEAGUE_PACE_DB)'}
              >
                {pick.pace_source === 'learned' ? 'CALCULADO' : 'DEFAULT'}
              </span>
            )}
          </div>
        </div>
      </td>

      {/* MARCADOR — abreviaturas pegadas a cada número para evitar
          ambigüedad. Orden: away_abbr away_score – home_score home_abbr.
          Coherente con título "AWAY @ HOME". */}
      <td className="num">
        <div className="score">
          <div className="s">
            <span className="team-abbr team-abbr-away">{pick.away_team || '?'}</span>
            <span className={homeWin ? 'lose' : 'win'}>{awayScore}</span>
            <span className="sep">–</span>
            <span className={homeWin ? 'win' : 'lose'}>{homeScore}</span>
            <span className="team-abbr team-abbr-home">{pick.home_team || '?'}</span>
          </div>
          <div className="total">Total {(awayScore + homeScore)} pts</div>
        </div>
      </td>

      {/* LÍNEA O/U (con sub-pill "TG enviada en X" si corresponde) */}
      <td className="num" onClick={e => e.stopPropagation()}>
        <div className="line-stack">
          {effectiveLinea > 0 && parseFloat(effectiveLinea) > 0 ? (
            <span className="line-cell">
              <span className="val">{effectiveLinea}</span>
              <LineStatusBadge
                lineSource={pick.linea_source ?? pick.line_source}
                lineTs={pick.linea_ts ?? pick.line_ts}
              />
            </span>
          ) : (
            <LineStatusBadge
              lineSource={pick.linea_source ?? pick.line_source}
              lineTs={pick.linea_ts ?? pick.line_ts}
            />
          )}
          {tgSent && tgLine != null && Number.isFinite(parseFloat(tgLine)) && (
            <span className="line-tg-sub" title="Línea al momento del envío al canal VIP">
              📩 TG enviada en {parseFloat(tgLine)}
            </span>
          )}
        </div>
      </td>

      {/* EDGE (verde si >0 OVER, azul si <0 UNDER, muted si 0; pill coloquial
          si visibilidad oculta). Mismo gate showProjection que la proyección
          tenía: visible Q1 min1+, oculto en TEMPRANO/SIN COBERTURA/HT/BT.
          Campo: pick.edge (ajustado por confidence del motor Python — NO
          la diferencia cruda proyección−línea). */}
      <td className="num">
        {showProjection ? (
          (() => {
            const edgeNum = parseFloat(pick.edge);
            const hasEdge = pick.edge != null && !isNaN(edgeNum);
            if (!hasEdge) return <span style={{ color: 'var(--muted-2)' }}>—</span>;
            const color = edgeNum > 0 ? 'var(--pos)'
                        : edgeNum < 0 ? 'var(--info)'
                        : 'var(--muted)';
            const sign = edgeNum >= 0 ? '+' : '';
            return (
              <span style={{ color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {sign}{edgeNum.toFixed(1)}
                <Tooltip text={getProjTooltip()} />
              </span>
            );
          })()
        ) : (
          <span className="proj-prelim" title="El motor mostrará el edge cuando tenga datos confiables">
            👀 Tranqui, ya verás mi predicción
          </span>
        )}
      </td>

      {/* VEREDICTO */}
      <td>
        {(() => {
          // FASE V7 — si /live_games trajo categoría server-side, manda esa.
          if (v6Categoria === 'ENTRAR') {
            const fbEdgeNum = parseFloat(pick.edge || 0);
            const sideRaw   = (pick.v6?.side || pick.side || '').toUpperCase();
            const side      = sideRaw === 'UNDER' || sideRaw === 'OVER'
              ? sideRaw
              : (fbEdgeNum < 0 ? 'UNDER' : 'OVER');
            return (
              <span
                className="verdict"
                data-v="ENTRAR"
                title={v6Razon || undefined}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <span aria-hidden="true" style={{ fontSize: '1em', lineHeight: 1 }}>⚡</span>
                <span>ENTRAR</span>
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '.04em',
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: side === 'UNDER' ? 'var(--neg-soft)' : 'var(--pos-soft)',
                    color:      side === 'UNDER' ? 'var(--neg)'      : 'var(--pos)',
                  }}
                >
                  {side}
                </span>
              </span>
            );
          }
          if (v6Categoria === 'SOLO_INFO') {
            return <span className="verdict" data-v="ESPERAR" title={v6Razon || undefined}>SOLO INFO</span>;
          }
          if (v6Categoria === 'DESCARTADO') {
            return <span className="verdict" data-v="DESCARTAR" title={v6Razon || undefined}>DESCARTADO</span>;
          }
          if (v6Categoria === 'VIGILAR' || v6Categoria === 'ESPERAR' || v6Categoria === 'TEMPRANO') {
            const dataV = v6Categoria === 'TEMPRANO' ? 'ESPERAR' : v6Categoria;
            return <span className="verdict" data-v={dataV} title={v6Razon || undefined}>{v6Categoria}</span>;
          }
          // Fallback legacy: lógica local cuando el bot no escribió a /live_games.
          if (isBlocked) {
            return (
              <span className="verdict verdict-blocked" title="Esperando datos para veredicto eficiente">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, flexShrink: 0 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span>BLOQUEADO</span>
                <span className="verdict-blocked-sub">Esperando datos</span>
              </span>
            );
          }
          return <span className="verdict" data-v={tone}>{displayVerdict}</span>;
        })()}
        {tgSent && (
          <span
            className="tg-badge"
            title="Alerta enviada al canal VIP de Telegram"
            aria-label="Telegram enviado"
          >
            ✈ TG
          </span>
        )}
      </td>
    </tr>
  );
}
